import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { 
  BarChart3, TrendingUp, Users, Store, DollarSign, 
  ArrowLeft, RefreshCw, Calendar, CheckCircle, XCircle,
  ArrowUpRight, ArrowDownRight, Activity, Banknote
} from 'lucide-react';
import { toast } from 'sonner';

import { API_URL } from '../../utils/apiConfig';

export default function AdminAnalytics() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('30d');

  useEffect(() => {
    if (token && user?.role === 'admin') {
      fetchAnalytics();
      fetchSummary();
    }
  }, [token, user, range]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/analytics/?range=${range}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await fetch(`${API_URL}/api/analytics/summary`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      }
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <XCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-bold text-slate-900">Access Denied</h2>
          <p className="text-slate-600 mt-2">This page is only available to administrators.</p>
          <Button onClick={() => navigate('/')} className="mt-4">Go Home</Button>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const kpis = analytics?.kpis || {};
  const flowSplit = analytics?.flow_split || {};
  const signups = analytics?.signups_by_role || {};
  const payoutStats = analytics?.payouts || {};
  const topMerchants = analytics?.top_merchants || [];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/app/admin')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Analytics Dashboard</h1>
              <p className="text-sm text-slate-500">Growth & Operations Metrics</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Date Range Selector */}
            <div className="flex bg-slate-100 rounded-lg p-1">
              {['7d', '30d', '90d'].map((r) => (
                <button
                  key={r}
                  onClick={() => setRange(r)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    range === r 
                      ? 'bg-white text-emerald-700 shadow-sm' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                  data-testid={`range-${r}`}
                >
                  {r}
                </button>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={fetchAnalytics}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Quick Summary Cards */}
            {summary && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-5 text-white">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-emerald-100 text-sm">Today&apos;s GMV</span>
                    <DollarSign className="w-5 h-5 text-emerald-200" />
                  </div>
                  <p className="text-2xl font-bold">{formatCurrency(summary.today?.gmv)}</p>
                  <p className="text-emerald-100 text-sm mt-1">{summary.today?.transactions || 0} transactions</p>
                </div>
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-5 text-white">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-blue-100 text-sm">This Month&apos;s GMV</span>
                    <TrendingUp className="w-5 h-5 text-blue-200" />
                  </div>
                  <p className="text-2xl font-bold">{formatCurrency(summary.this_month?.gmv)}</p>
                  <p className="text-blue-100 text-sm mt-1">{summary.this_month?.transactions || 0} transactions</p>
                </div>
                <div className="bg-gradient-to-br from-slate-700 to-slate-800 rounded-2xl p-5 text-white">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-slate-300 text-sm">All Time</span>
                    <Activity className="w-5 h-5 text-slate-400" />
                  </div>
                  <p className="text-2xl font-bold">{summary.all_time?.transactions?.toLocaleString() || 0}</p>
                  <p className="text-slate-300 text-sm mt-1">{summary.all_time?.users || 0} total users</p>
                </div>
              </div>
            )}

            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="kpi-grid">
              <div className="bg-white rounded-xl border border-slate-100 p-4 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-500">GMV ({range})</span>
                  <DollarSign className="w-4 h-4 text-emerald-600" />
                </div>
                <p className="text-xl font-bold text-slate-900">{formatCurrency(kpis.gmv)}</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-100 p-4 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-500">Transactions</span>
                  <BarChart3 className="w-4 h-4 text-blue-600" />
                </div>
                <p className="text-xl font-bold text-slate-900">{kpis.transaction_count?.toLocaleString() || 0}</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-100 p-4 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-500">Avg. Transaction</span>
                  <TrendingUp className="w-4 h-4 text-purple-600" />
                </div>
                <p className="text-xl font-bold text-slate-900">{formatCurrency(kpis.average_transaction)}</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-100 p-4 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-500">Success Rate</span>
                  <CheckCircle className="w-4 h-4 text-green-600" />
                </div>
                <p className="text-xl font-bold text-slate-900">{kpis.success_rate || 100}%</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-100 p-4 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-500">Active Supporters</span>
                  <Users className="w-4 h-4 text-orange-600" />
                </div>
                <p className="text-xl font-bold text-slate-900">{kpis.active_senders || 0}</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-100 p-4 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-500">Active Merchants</span>
                  <Store className="w-4 h-4 text-teal-600" />
                </div>
                <p className="text-xl font-bold text-slate-900">{kpis.active_merchants || 0}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Flow Split */}
              <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="flow-split-card">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-emerald-600" />
                  Payment Flow Split
                </h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-600">Colmado Payments</span>
                      <span className="font-medium text-slate-900">{flowSplit.colmado_payments?.percentage || 0}%</span>
                    </div>
                    <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                        style={{ width: `${flowSplit.colmado_payments?.percentage || 0}%` }}
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{flowSplit.colmado_payments?.count || 0} transactions</p>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-600">Colmado Authorizations</span>
                      <span className="font-medium text-slate-900">{flowSplit.family_transfers?.percentage || 0}%</span>
                    </div>
                    <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-500 rounded-full transition-all duration-500"
                        style={{ width: `${flowSplit.family_transfers?.percentage || 0}%` }}
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{flowSplit.family_transfers?.count || 0} transactions</p>
                  </div>
                </div>
              </div>

              {/* New Signups */}
              <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="signups-card">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  New Signups ({range})
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-slate-50 rounded-xl">
                    <p className="text-2xl font-bold text-slate-900">{signups.sender || 0}</p>
                    <p className="text-sm text-slate-600">Supporters</p>
                  </div>
                  <div className="text-center p-4 bg-slate-50 rounded-xl">
                    <p className="text-2xl font-bold text-slate-900">{signups.merchant || 0}</p>
                    <p className="text-sm text-slate-600">Merchants</p>
                  </div>
                  <div className="text-center p-4 bg-slate-50 rounded-xl">
                    <p className="text-2xl font-bold text-slate-900">{signups.admin || 0}</p>
                    <p className="text-sm text-slate-600">Admins</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Total New Users</span>
                    <span className="text-lg font-bold text-emerald-600">{kpis.new_signups || 0}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Merchants */}
              <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="top-merchants-card">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Store className="w-5 h-5 text-emerald-600" />
                  Top Merchants by Volume
                </h3>
                {topMerchants.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Store className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p>No merchant data yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {topMerchants.slice(0, 5).map((merchant, index) => (
                      <div key={merchant.wallet_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 flex items-center justify-center bg-emerald-100 text-emerald-700 text-xs font-bold rounded-full">
                            {index + 1}
                          </span>
                          <div>
                            <p className="font-medium text-slate-900">{merchant.merchant_name}</p>
                            <p className="text-xs text-slate-500">{merchant.transaction_count} transactions</p>
                          </div>
                        </div>
                        <span className="font-bold text-emerald-600">{formatCurrency(merchant.volume)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Payout Stats */}
              <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="payout-stats-card">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Banknote className="w-5 h-5 text-blue-600" />
                  Payout Summary ({range})
                </h3>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="text-center p-4 bg-blue-50 rounded-xl">
                    <p className="text-2xl font-bold text-blue-900">{formatCurrency(payoutStats.total_amount)}</p>
                    <p className="text-sm text-blue-600">Total Payouts</p>
                  </div>
                  <div className="text-center p-4 bg-slate-50 rounded-xl">
                    <p className="text-2xl font-bold text-slate-900">{payoutStats.count || 0}</p>
                    <p className="text-sm text-slate-600">Payout Count</p>
                  </div>
                </div>
                
                {Object.keys(payoutStats.status_distribution || {}).length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-2">Status Distribution</p>
                    <div className="space-y-2">
                      {Object.entries(payoutStats.status_distribution || {}).map(([status, count]) => (
                        <div key={status} className="flex items-center justify-between text-sm">
                          <span className="capitalize text-slate-600">{status.replace(/_/g, ' ')}</span>
                          <span className="font-medium text-slate-900">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* GMV Timeline */}
            {analytics?.timelines?.gmv?.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="gmv-timeline-card">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                  GMV Over Time
                </h3>
                <div className="h-48 flex items-end gap-1">
                  {analytics.timelines.gmv.slice(-30).map((day, index) => {
                    const maxGmv = Math.max(...analytics.timelines.gmv.map(d => d.gmv));
                    const height = maxGmv > 0 ? (day.gmv / maxGmv) * 100 : 0;
                    return (
                      <div 
                        key={day.date} 
                        className="flex-1 bg-emerald-500 rounded-t hover:bg-emerald-600 transition-colors cursor-pointer group relative"
                        style={{ height: `${Math.max(height, 2)}%` }}
                        title={`${day.date}: ${formatCurrency(day.gmv)}`}
                      >
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-slate-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                          {day.date}: {formatCurrency(day.gmv)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </main>
    </div>
  );
}
